<template>
  <div v-if="loading" class="flex items-center justify-center h-full">
    <loading-spinner diameter="64px"/>
  </div>

  <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else>
    <img
        style="display: none"
        @load="loadFooter()"
        src="@/assets/images/examination_footer.svg"
        alt=""
        ref="footerImage"
    />
    <img
        style="display: none"
        @load="loadLogo()"
        src="@/assets/images/logo_examination.svg"
        alt=""
        ref="logoImage"
    />
    <div class="receipt-size-bar mx-3 mt-3 flex flex-row items-center gap-2">
      <el-radio-group dir="ltr" model-value="card" size="small" @update:model-value="selectSize">
        <el-radio-button v-for="size in ['a4', 'a5', 'a6']" :key="size" :value="size" :label="size">{{ size.toUpperCase() }}</el-radio-button>
        <el-radio-button value="card" label="card">{{ t('card') }}</el-radio-button>
      </el-radio-group>
    </div>
    <div class="flex flex-row">
      <div class="flex flex-col">
         <action-button :title="t('print')" faIcon="print" @click="print" />
        <action-button :title="t('download')" faIcon="download"
                       @click="download"/>
      </div>

      <div class="flex flex-col items-center mx-4 mt-3 px-2 relative"
           style="direction: ltr">
        <div ref="container" id="container"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {useI18n} from 'vue-i18n'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import Examination, {
  examinationI18nMessages
} from '@/models/customer/Examination'
import {formatPostgresDate, parseFormat} from '@/utils/date'
import ActionButton from '@/components/ActionButton.vue'

import {renderIDCard} from '@/utils/idCardRender'
import {useSettingsStore} from "@/stores/settingsStore";

export default defineComponent({
  components: {
    LoadingSpinner,
    AlertBadge,
    ActionButton
  },

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          card: 'Card',
          ...examinationI18nMessages.en,
          print: 'Print',
          download: 'Download'
        },
        ar: {
          card: 'Card',
          ...examinationI18nMessages.ar,
          print: 'اطبع',
          download: 'تحميل'
        }
      }
    })

    return {
      t,
      parseFormat,
      formatPostgresDate,
      useSettingsStore
    }
  },
  data() {
    return {
      originalTitle: document.title,
      stage: null as any,
      examination: null as Examination | null,
      loading: true,
      error: {
        title: '',
        body: ''
      }
    }
  },
  async mounted() {
    if (useSettingsStore().getValue('hide_examination_id_print') != 'false') {
      this.$router.push('/');
      return;
    }
    if (this.$route.params.examination_id) {
      // get order
      try {
        const resp = await this.$http.get<Examination>(
            `${Examination.ENDPOINT}/${this.$route.params.examination_id}`
        )
        this.examination = Examination.from(resp)
        document.title = `Thecky ${this.examination.externalID} | ${formatPostgresDate(String(this.examination.createdAt), 'DD-MM-YYYY')}`
      } catch (error) {
        this.error.title = error.title
        this.error.body = error.body
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.setup()
        })
      }
    }
  },
  beforeUnmount() {
    document.title = this.originalTitle
  },
  methods: {
    // Back to the printout in the picked paper size
    selectSize(size: string | number | boolean | undefined) {
      if (size === 'card') return
      this.$router.replace({name: 'examination-receipt', params: {examination_id: this.$route.params.examination_id}, query: {size: String(size)}})
    },
    loadFooter() {
      // const footer = new Konva.Image({
      //   x: 45,
      //   y: this.stage.height() - 80,
      //   /* @ts-ignore */
      //   image: this.$refs['footerImage'],
      //   height: 17,
      //   width: 300,
      //   scaleX: 2.5,
      //   scaleY: 2.5,
      //   draggable: false
      // })
      // this.layer.add(footer)
    },
    loadLogo() {
      // const logo = new Konva.Image({
      //   x: this.stage.width() / 2 - 20,
      //   y: this.stage.height() - 110,
      //   /* @ts-ignore */
      //   image: this.$refs['logoImage'],
      //   scaleX: 4,
      //   scaleY: 4,
      //   draggable: false
      // })
      // this.layer.add(logo)
    },
    async setup() {
      const settings = useSettingsStore()
      const showQR = settings.getValue('show_qr_codes_on_eye_examination_reports') == 'true'
      const clientKey = settings.getValue('viewer_client_key') || 'test'
      this.stage = await renderIDCard({
        /* @ts-ignore */
        container: this.$refs['container'],
        exam: this.examination,
        style: settings.getValue('examination_id_card_style'),
        qrUrl: showQR ? `https://exams.thecky.com/${clientKey}/${this.$route.params.examination_id}` : null,
        addReadingToDistance: settings.getValue('adding_reading_glasses_degrees_to_distances_when_printing') == 'true'
      })
    },
    downloadURI(uri: any, name: any) {
      const link = document.createElement('a')
      link.download = name
      link.href = uri
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    async download() {
      const dataURL = this.stage.toDataURL({pixelRatio: 3})
      this.downloadURI(dataURL, `${this.examination?.externalID}-idcard.png`)
    },
    print() {
      const dataURL = this.stage.toDataURL({pixelRatio: 3})
      const html = `
                <!DOCTYPE html>
                <html lang="en">
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>Print Image</title>
                    <style>
                        /* Define any necessary styles here */
                        body { margin: 0; padding: 0; }
                        img { max-width: 100%; }
                    </style>
                </head>
                <body>
                    <!-- Insert the image to be printed -->
                    <img src="${dataURL}" alt="Example Image">
                </body>
                </html>
            `

      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.open()
        printWindow.document.write(html)
        printWindow.document.close()

        const i = setInterval(() => {
          /* @ts-ignore */
          const content = printWindow.document.querySelector('body').innerHTML
          if (content.length) {
            printWindow.print()
            clearInterval(i)
            setTimeout(() => {
              printWindow.close()
            }, 100)
          }
        }, 200)
      }
    }
  }
})
</script>
<style>
/* The Arabic theme (rtlcss) mirrors radio-button borders; keep the size bar drawn LTR */
.receipt-size-bar .el-radio-button__inner {
  border-left: 0;
  border-right: var(--el-border);
  border-radius: 0;
}

.receipt-size-bar .el-radio-button:first-child .el-radio-button__inner {
  border-left: var(--el-border);
  border-radius: var(--el-border-radius-base) 0 0 var(--el-border-radius-base);
}

.receipt-size-bar .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 0 var(--el-border-radius-base) var(--el-border-radius-base) 0;
}

.receipt-size-bar .el-radio-button.is-active .el-radio-button__inner {
  border-color: var(--el-radio-button-checked-border-color, var(--el-color-primary));
  box-shadow: -1px 0 0 0 var(--el-radio-button-checked-border-color, var(--el-color-primary));
}

#container {
  border-radius: 10px;
  overflow: hidden;
}
</style>
